import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { PencilSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle } from "@libs/zapi-ui/shadcn";
import * as DsDialog from "@libs/zapi-ui/ds/components/dialog";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { ProjectDefaultLogo, ProjectShapeIcon, getProjectIconPalette, isProjectShape } from "@libs/zapi-ui/const";
import { cn } from "@/zapi-frontend/shared/lib/utils";

type TProjectIconProps = {
  shape?: string | null;
  color?: string | null;
  logoUrl?: string | null;
  className?: string;
};

export function ProjectIcon({ shape, color, logoUrl, className }: TProjectIconProps) {
  // The photograph outranks the shape: a generated project gets a picture of its subject,
  // and the shape is what it falls back to when the search found nothing.
  if (logoUrl) {
    return <img src={logoUrl} alt="" className={cn("shrink-0 object-cover", className)} />;
  }
  if (isProjectShape(shape)) {
    const palette = getProjectIconPalette(color);
    return (
      <div
        className={cn("shrink-0 flex items-center justify-center", className)}
        style={{ backgroundColor: palette.iconBackground, color: palette.icon }}
      >
        <ProjectShapeIcon shape={shape} className="size-[62%]" />
      </div>
    );
  }
  return (
    <div className={cn("shrink-0 flex items-center justify-center bg-innoveraZinc-100", className)}>
      <ProjectDefaultLogo className="size-[62%] text-innoveraZinc-400" />
    </div>
  );
}

type TAppearance = "default" | "design-system";

type TProjectPhotoLightboxProps = {
  logoUrl: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appearance: TAppearance;
};

function ProjectPhotoLightbox({ logoUrl, open, onOpenChange, appearance }: TProjectPhotoLightboxProps) {
  const { t } = useTranslation(["projectHome", "common"]);
  if (appearance === "design-system") {
    return (
      <DsDialog.Dialog open={open} onOpenChange={onOpenChange}>
        <DsDialog.DialogContent
          closeLabel={t("common:close")}
          data-testid="project-photo-lightbox"
          className="w-auto max-w-[90vw] border-none bg-transparent p-0 shadow-none [&>button]:end-2 [&>button]:top-2 [&>button]:rounded-full [&>button]:p-1 [&>button]:opacity-100"
        >
          <DsDialog.DialogTitle className="sr-only">{t("projectSetup.photoExpand")}</DsDialog.DialogTitle>
          <img src={logoUrl} alt="" className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain" />
        </DsDialog.DialogContent>
      </DsDialog.Dialog>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="project-photo-lightbox"
        className="w-auto max-w-[90vw] border-none bg-transparent p-0 shadow-none [&>button]:bg-white [&>button]:opacity-100 [&>button]:rounded-full [&>button]:p-1 [&>button]:right-2 [&>button]:top-2"
      >
        <DialogTitle className="sr-only">{t("projectSetup.photoExpand")}</DialogTitle>
        <img src={logoUrl} alt="" className="max-h-[85vh] max-w-[90vw] rounded-lg object-contain" />
      </DialogContent>
    </Dialog>
  );
}

export const ACCEPTED_PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ACCEPTED_PHOTO_ATTRIBUTE = ACCEPTED_PHOTO_TYPES.join(",");
export const MAX_PHOTO_SIZE_BYTES = 5 * 1024 * 1024;

export function readAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

type TProjectIconEditorProps = {
  shape: string | null;
  color: string | null;
  logoUrl?: string | null;
  onPhotoChange: (logoBase64: string) => void | Promise<void>;
  readOnly?: boolean;
  className?: string;
  // `design-system` renders the photo lightbox and the upload spinner with the design system, for a
  // migrated part.
  appearance?: TAppearance;
};

export function ProjectIconEditor({
  shape,
  color,
  logoUrl,
  onPhotoChange,
  readOnly = false,
  className,
  appearance = "default",
}: TProjectIconEditorProps) {
  const { t } = useTranslation(["projectHome", "common"]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const icon = <ProjectIcon shape={shape} color={color} logoUrl={logoUrl} className={cn("size-[60px]", className)} />;

  const lightbox = logoUrl ? (
    <ProjectPhotoLightbox
      logoUrl={logoUrl}
      open={isExpanded}
      onOpenChange={setIsExpanded}
      appearance={appearance}
    />
  ) : null;

  if (readOnly) {
    if (!logoUrl) return icon;
    return (
      <>
        <button
          type="button"
          data-testid="project-photo-expand-button"
          aria-label={t("projectSetup.photoExpand")}
          onClick={() => setIsExpanded(true)}
          className="block w-fit shrink-0 cursor-zoom-in outline-hidden"
        >
          {icon}
        </button>
        {lightbox}
      </>
    );
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared so picking the same file twice in a row still fires a change event.
    event.target.value = "";
    if (!file) return;

    if (!ACCEPTED_PHOTO_TYPES.includes(file.type)) {
      toast.error(t("projectSetup.photoInvalidType"));
      return;
    }
    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      toast.error(t("projectSetup.photoTooLarge"));
      return;
    }

    setIsUploading(true);
    try {
      await onPhotoChange(await readAsDataUrl(file));
    } catch {
      toast.error(t("projectSetup.photoUploadFailed"));
    } finally {
      setIsUploading(false);
    }
  };

  const openPicker = () => inputRef.current?.click();

  const badgeClassName =
    "absolute bottom-[3px] right-[3px] flex items-center justify-center size-6 rounded-md border border-innoveraZinc-150 bg-white text-innoveraZinc-900 shadow-xs opacity-0 transition-opacity group-hover:opacity-100";

  const uploadingOverlay = (
    <span className="absolute inset-0 flex items-center justify-center bg-white/70">
      <Spinner aria-label={t("common:loading")} className="size-5 text-muted-foreground" />
    </span>
  );

  return (
    <>
      {logoUrl ? (
        <div className="relative group w-fit shrink-0">
          <button
            type="button"
            data-testid="project-photo-expand-button"
            aria-label={t("projectSetup.photoExpand")}
            disabled={isUploading}
            onClick={() => setIsExpanded(true)}
            className="block w-fit shrink-0 cursor-zoom-in outline-hidden"
          >
            {icon}
          </button>
          {isUploading ? (
            uploadingOverlay
          ) : (
            <button
              type="button"
              data-testid="project-photo-upload-button"
              aria-label={t("projectSetup.photoUpload")}
              onClick={openPicker}
              className={cn(badgeClassName, "outline-hidden")}
            >
              <PencilSimple className="size-3.5" weight="regular" />
            </button>
          )}
        </div>
      ) : (
        <button
          type="button"
          data-testid="project-photo-upload-button"
          aria-label={t("projectSetup.photoUpload")}
          disabled={isUploading}
          onClick={openPicker}
          className="relative group block w-fit shrink-0 outline-hidden"
        >
          {icon}
          {isUploading ? (
            uploadingOverlay
          ) : (
            <span className={badgeClassName}>
              <PencilSimple className="size-3.5" weight="regular" />
            </span>
          )}
        </button>
      )}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        data-testid="project-photo-upload-input"
        accept={ACCEPTED_PHOTO_ATTRIBUTE}
        onChange={handleFileChange}
      />
      {lightbox}
    </>
  );
}
